import { EMPTY_VALUE_PLACEHOLDER, isValidDate } from "@shared";
import { endOfDay, startOfDay } from "date-fns";

import { AppointmentForTakeoverResponse } from "@/api";
import { AppointmentLabels } from "@/common/hooks/useAppointmentLabels";

/** VON column: the current holder (the counselor the appointment is currently assigned to). */
export function getFromUserCode(row: AppointmentForTakeoverResponse): string {
  return row.assignedCounselorUserCode ?? EMPTY_VALUE_PLACEHOLDER;
}

/** True once the appointment has been taken over at least once (it has a previous holder). */
export function isTakenOver(row: AppointmentForTakeoverResponse): boolean {
  return row.previousCounselorUserCode != null;
}

/** ÜBERNOMMEN VON column: the previous holder it was taken over from (empty if never taken over). */
export function getTakenOverByUserCode(row: AppointmentForTakeoverResponse): string | null {
  return row.previousCounselorUserCode ?? null;
}

/** Whether the appointment belongs to that counselor: the current holder the VON column shows. */
export function isFromCounselor(row: AppointmentForTakeoverResponse, counselorId: string): boolean {
  return row.assignedCounselorId === counselorId;
}

/**
 * Whether the appointment falls inside the picked from/to date window ("Datum von" / "Datum bis").
 * Both bounds are optional and cover the whole calendar day, so picking the same date in both fields
 * keeps that day. A half-typed date reaches us as an Invalid Date and is ignored — every comparison
 * against it is false, which would otherwise silently pass every row.
 */
export function isWithinDateFilter(row: AppointmentForTakeoverResponse, from: Date | null, to: Date | null): boolean {
  if (isValidDate(from) && row.startTimeUtc < startOfDay(from)) {
    return false;
  }
  if (isValidDate(to) && row.startTimeUtc > endOfDay(to)) {
    return false;
  }
  return true;
}

/** Minimal shape needed to build the DETAILS label — satisfied by both takeover and calendar responses. */
export interface ComposableAppointment {
  purposeKey: string;
  clientFirstName?: string | null;
  clientLastName?: string | null;
  location?: string | null;
  details?: string | null;
}

/** DETAILS column: "<Dienst>, <Nachname Vorname>" for client appointments, else free details or location. */
export function composeDetails(row: ComposableAppointment, labels: AppointmentLabels): string {
  const purpose = labels.purposeLabelOf(row.purposeKey);
  const client = [row.clientLastName, row.clientFirstName].filter(Boolean).join(" ").trim();
  if (client) {
    return `${purpose}, ${client}`;
  }
  if (row.details) {
    return row.details;
  }
  if (row.location) {
    return `${purpose} ${row.location ?? ""}`;
  }
  return purpose;
}
